import { Typography } from '@neo4j-ndl/react';
import classNames from 'classnames';
import { forwardRef } from 'react';

type GalleryTileProps = {
  className?: string;
  previewClassName?: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  headerTrailingContent?: React.ReactNode;
  footer?: React.ReactNode;
  ariaLabel: string;
  onClick?: () => void;
  preview?: React.ReactNode;
  htmlAttributes?: React.HTMLAttributes<HTMLDivElement>;
};

export const GalleryTile = forwardRef<HTMLDivElement, GalleryTileProps>(
  (
    {
      className,
      previewClassName,
      preview,
      title,
      description,
      headerTrailingContent,
      footer,
      ariaLabel,
      onClick,
      htmlAttributes,
    },
    ref,
  ) => {
    const classes = classNames(
      'border-neutral-border-weak bg-neutral-bg-weak outline-palette-primary-focus relative flex flex-col gap-3 rounded-3xl border p-3 pt-2',
      onClick && 'cursor-pointer hover:border-neutral-border-strong',
      className,
    );

    return (
      <div
        ref={ref}
        className={classes}
        {...(onClick && {
          tabIndex: 0,
          role: 'button',
          onClick,
          onTouchStart: onClick,
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onClick();
            }
          },
        })}
        aria-label={ariaLabel}
        {...htmlAttributes}
      >
        <div className="flex w-full flex-row items-start justify-between gap-1 pl-1">
          <div className="flex min-w-0 grow flex-col pt-1.5">
            {title}
            {Boolean(description) && (
              <Typography variant="body-small" className="text-palette-neutral-text-weak truncate">
                {description}
              </Typography>
            )}
          </div>
          {Boolean(headerTrailingContent) && (
            <div
              className="flex shrink-0 flex-row items-center gap-2"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {headerTrailingContent}
            </div>
          )}
        </div>
        <div className={classNames('rounded-1xl bg-neutral-bg-default h-64 overflow-hidden', previewClassName)}>
          {preview}
        </div>

        {Boolean(footer) && (
          <div className="flex flex-row items-center gap-1 overflow-hidden truncate px-1">{footer}</div>
        )}
      </div>
    );
  },
);

GalleryTile.displayName = 'GalleryTile';
